import FormLabel from '@material-ui/core/FormLabel';
import { RadioGroup } from '@material-ui/core';
import addonPhotoTemplate from 'assets/img/addonPhotoTemplate.jpg';
import CustomInput from 'components/CustomInput/CustomInput';
import FormImageUpload from 'components/FormImageUpload/FormImageUpload';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import SelectInput from 'components/FormSelect/SelectInput';
import Tooltip from '@material-ui/core/Tooltip';
import makeStyles from '@material-ui/styles/makeStyles';
import CardBody from 'components/Card/CardBody';
import { Info } from '@material-ui/icons';
import CardWrapper from 'components/Card/CardWrapper';
import { useTranslation } from 'react-i18next';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle.jsx';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import classNames from 'classnames';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import FormTextInputTranslatable from 'components/FormTextInput/FormTextInputTranslatable';
import LabeledRadio from 'components/LabeledRadio/LabeledRadio';
import InfoWithTooltip from 'components/InfoWithTooltip/InfoWithTooltip';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import EXTERNAL_URLS from 'constants/externalUrls';
import {
  darkNavyColor,
  grayColor,
} from 'assets/jss/material-dashboard-pro-react';

import { ADDON_TYPES } from './addonTypes';

const defaultUsageOptions = [
  { name: 'form.field.defaultUsage.option.forBag', value: 'FOR_BAG' },
  { name: 'form.field.defaultUsage.option.forDiet', value: 'FOR_DIET' },
];

const excludedPlacesOptions = [
  { name: 'form.field.excludedPlaces.option.newOrder', value: 'NEW_ORDER' },
  { name: 'form.field.excludedPlaces.option.extendBag', value: 'EXTEND_BAG' },
];

const useStyles = makeStyles({
  ...extendedFormsStyle,
  ...buttonsStyle,
  tooltip: { marginLeft: '5px', fontSize: '1rem' },
  priorityLabel: { marginBottom: '9px' },
  codeLabel: { marginBottom: '7px' },
  radioOptionRoot: {
    margin: '0 0 14px',
    alignItems: 'flex-start',
    '& .MuiRadio-root.Mui-checked': {
      color: `${darkNavyColor} !important`,
    },
    '& .MuiRadio-root.Mui-checked svg': {
      borderColor: `${darkNavyColor} !important`,
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },
  radioLabelWrapper: {
    display: 'block',
    marginTop: '2px',
  },
  radioTitle: {
    color: grayColor[6],
    fontSize: '14px',
    lineHeight: '1.3',
    fontWeight: 500,
    margin: 0,
  },
  radioTitleRow: {
    display: 'flex',
    alignItems: 'center',
  },
  radioDescription: {
    margin: '6px 0 0',
    color: grayColor[1],
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '1.45',
  },
  radioSectionDisabled: {
    '&, & *': {
      cursor: 'not-allowed !important',
    },
    '& .MuiRadio-root.Mui-checked': {
      color: `${grayColor[0]} !important`,
    },
    '& .MuiRadio-root.Mui-checked svg': {
      borderColor: `${grayColor[0]} !important`,
    },
  },
  radioTitleDisabled: {
    color: grayColor[0],
  },
  radioDescriptionDisabled: {
    color: grayColor[0],
  },
  disabledField: {
    '&, & *': {
      cursor: 'not-allowed !important',
    },
  },
  checkboxColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
});

const ClientPanel = ({
  formData,
  handleChange,
  handleCheckboxClick,
  categories,
  getImage,
  removeImage,
  contractors,
  diets,
  subAddons,
  addonType,
  isAddonTypeDisabled,
  canEditClientFields,
  canSelectRequiredAddons,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <>
      <CardWrapper
        title={t('orders.kind')}
        tooltip={
          <InfoWithTooltip
            link={EXTERNAL_URLS.subAddons}
            title={t('views.addons.addons.form.field.type.tooltip')}
          />
        }
      >
        <CardBody>
          <GridContainer>
            <GridItem sm={12}>
              <RadioGroup
                name="type"
                value={addonType}
                onChange={handleChange}
                className={
                  isAddonTypeDisabled ? classes.radioSectionDisabled : ''
                }
              >
                <LabeledRadio
                  FormControlLabelProps={{
                    classes: { root: classes.radioOptionRoot },
                  }}
                  value={ADDON_TYPES.ADDON}
                  disabled={isAddonTypeDisabled}
                  label={
                    <div className={classes.radioLabelWrapper}>
                      <div className={classes.radioTitleRow}>
                        <p
                          className={classNames(
                            classes.radioTitle,
                            isAddonTypeDisabled
                              ? classes.radioTitleDisabled
                              : ''
                          )}
                        >
                          {t(
                            'views.addons.addons.form.field.type.option.addon.label'
                          )}
                        </p>
                        <InfoIconTooltip
                          title={t(
                            'views.addons.addons.form.field.type.option.addon.tooltip'
                          )}
                        />
                      </div>
                      <p
                        className={classNames(
                          classes.radioDescription,
                          isAddonTypeDisabled
                            ? classes.radioDescriptionDisabled
                            : ''
                        )}
                      >
                        {t(
                          'views.addons.addons.form.field.type.option.addon.description'
                        )}
                      </p>
                    </div>
                  }
                />
                <LabeledRadio
                  FormControlLabelProps={{
                    classes: { root: classes.radioOptionRoot },
                  }}
                  value={ADDON_TYPES.SUB_ADDON}
                  disabled={isAddonTypeDisabled}
                  label={
                    <div className={classes.radioLabelWrapper}>
                      <p
                        className={classNames(
                          classes.radioTitle,
                          isAddonTypeDisabled ? classes.radioTitleDisabled : ''
                        )}
                      >
                        {t(
                          'views.addons.addons.form.field.type.option.subAddon.label'
                        )}
                      </p>
                      <p
                        className={classNames(
                          classes.radioDescription,
                          isAddonTypeDisabled
                            ? classes.radioDescriptionDisabled
                            : ''
                        )}
                      >
                        {t(
                          'views.addons.addons.form.field.type.option.subAddon.description'
                        )}
                      </p>
                    </div>
                  }
                />
              </RadioGroup>
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>
      <CardWrapper title={t('form.basicInfo')}>
        <CardBody>
          <GridContainer>
            <GridItem sm={8}>
              <GridContainer>
                <GridItem sm={12}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.name.label')} *
                    <Tooltip
                      className={classes.tooltip}
                      title={t('views.addons.addons.form.field.name.tooltip')}
                      placement="right"
                    >
                      <Info fontSize="small" />
                    </Tooltip>
                  </FormLabel>
                  <FormTextInputTranslatable
                    name="name"
                    value={formData.name}
                    handleChange={handleChange}
                  />
                </GridItem>
                <GridItem
                  sm={12}
                  className={!canEditClientFields && classes.disabledField}
                >
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.shortDescription.label')}
                    <Tooltip
                      className={classes.tooltip}
                      title={t(
                        'views.addons.addons.form.field.shortDescription.tooltip'
                      )}
                      placement="right"
                    >
                      <Info fontSize="small" />
                    </Tooltip>
                  </FormLabel>
                  <FormTextInputTranslatable
                    isRequired={false}
                    name="shortDescription"
                    value={formData.shortDescription}
                    handleChange={handleChange}
                    disabled={!canEditClientFields}
                  />
                </GridItem>
                <GridItem
                  sm={3}
                  className={classNames(
                    classes.checkboxColumn,
                    !canEditClientFields && classes.disabledField
                  )}
                >
                  <LabeledCheckbox
                    name="active"
                    onClick={handleCheckboxClick}
                    checked={formData.active}
                    disabled={!canEditClientFields}
                    label={
                      <>
                        {t('form.field.active.label')}
                        <Tooltip
                          className={classes.tooltip}
                          title={t(
                            'views.addons.addons.form.field.active.tooltip'
                          )}
                          placement="right"
                        >
                          <Info fontSize="small" />
                        </Tooltip>
                      </>
                    }
                  />
                  <LabeledCheckbox
                    name="availableInShop"
                    onClick={handleCheckboxClick}
                    checked={formData.availableInShop}
                    disabled={!canEditClientFields}
                    label={
                      <>
                        {t('form.field.availableInShop.label')}
                        <Tooltip
                          className={classes.tooltip}
                          title={t(
                            'views.addons.addons.form.field.availableInShop.tooltip'
                          )}
                          placement="right"
                        >
                          <Info fontSize="small" />
                        </Tooltip>
                      </>
                    }
                  />
                </GridItem>
                <GridItem
                  sm={5}
                  className={!canEditClientFields && classes.disabledField}
                >
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.excludedPlaces.label')}
                    <Tooltip
                      className={classes.tooltip}
                      title={t(
                        'views.addons.addons.form.field.excludedPlaces.tooltip'
                      )}
                      placement="right"
                    >
                      <Info fontSize="small" />
                    </Tooltip>
                  </FormLabel>
                  <SelectInput
                    noGrid={true}
                    multiple={true}
                    classes={classes}
                    name="excludedPlaces"
                    mapBy="name"
                    trackBy="value"
                    options={excludedPlacesOptions.map(({ name, value }) => ({
                      name: t(name),
                      value,
                    }))}
                    value={formData.excludedPlaces}
                    handleChange={handleChange}
                    disabled={!canEditClientFields}
                  />
                </GridItem>
                <GridItem
                  sm={4}
                  className={!canEditClientFields && classes.disabledField}
                >
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.defaultUsage.label')}
                  </FormLabel>
                  <SelectInput
                    noGrid={true}
                    classes={classes}
                    name="defaultUsage"
                    mapBy="name"
                    trackBy="value"
                    options={defaultUsageOptions.map(({ name, value }) => ({
                      name: t(name),
                      value,
                    }))}
                    value={formData.defaultUsage}
                    handleChange={handleChange}
                    disabled={!canEditClientFields}
                  />
                </GridItem>

                <GridItem
                  sm={3}
                  className={!canEditClientFields && classes.disabledField}
                >
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.category.label')}
                    <Tooltip
                      className={classes.tooltip}
                      title={t(
                        'views.addons.addons.form.field.category.tooltip'
                      )}
                      placement="right"
                    >
                      <Info fontSize="small" />
                    </Tooltip>
                  </FormLabel>
                  <SelectInput
                    noGrid={true}
                    classes={classes}
                    name="category"
                    mapBy="name"
                    options={categories}
                    value={formData.category}
                    handleChange={handleChange}
                    disabled={!canEditClientFields}
                  />
                </GridItem>
                <GridItem
                  sm={3}
                  className={!canEditClientFields && classes.disabledField}
                >
                  <FormLabel
                    className={classNames(
                      classes.labelHorizontal,
                      classes.priorityLabel
                    )}
                  >
                    {t('form.field.priority.label')}
                    <Tooltip
                      className={classes.tooltip}
                      title={t(
                        'views.addons.addons.form.field.priority.tooltip'
                      )}
                      placement="right"
                    >
                      <Info fontSize="small" />
                    </Tooltip>
                  </FormLabel>
                  <CustomInput
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      name: 'position',
                      value: formData.position,
                      onChange: handleChange,
                      type: 'number',
                    }}
                    disabled={!canEditClientFields}
                  />
                </GridItem>
                <GridItem sm={3}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.clientCost.label')} *
                  </FormLabel>
                  <CustomInput
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      name: 'clientCost',
                      value: formData.clientCost,
                      onChange: handleChange,
                      type: 'number',
                      style: { marginTop: '9px' },
                    }}
                  />
                </GridItem>
                <GridItem sm={3}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.clientVat.label')} *
                    <Tooltip
                      className={classes.tooltip}
                      title={t(
                        'views.addons.addons.form.field.clientVat.tooltip'
                      )}
                      placement="right"
                    >
                      <Info fontSize="small" />
                    </Tooltip>
                  </FormLabel>
                  <CustomInput
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      name: 'clientVat',
                      value: formData.clientVat,
                      onChange: handleChange,
                      type: 'number',
                      style: { marginTop: '9px' },
                    }}
                  />
                </GridItem>
                <GridItem
                  sm={12}
                  className={
                    canSelectRequiredAddons ? undefined : classes.disabledField
                  }
                >
                  <FormLabel className={classes.labelHorizontal}>
                    {t('views.addons.addons.form.field.linkedSubAddon.label')}
                  </FormLabel>
                  <SelectInput
                    noGrid={true}
                    classes={classes}
                    name="requiredAddon"
                    mapBy="name"
                    options={[
                      { '@id': null, name: t('common.lack') },
                      ...(subAddons || []),
                    ]}
                    value={formData.requiredAddon}
                    handleChange={handleChange}
                    disabled={!canSelectRequiredAddons}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>

            <GridItem sm={4}>
              <GridContainer>
                <GridItem sm={12}>
                  <FormImageUpload
                    stateName="image"
                    getImage={getImage}
                    removeImage={removeImage}
                    previewUrl={formData?.imageUrl}
                    defaultImage={addonPhotoTemplate}
                  />
                </GridItem>
                <GridItem
                  sm={12}
                  className={!canEditClientFields && classes.disabledField}
                >
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.restrictedToDiets.label')}
                    <Tooltip
                      className={classes.tooltip}
                      title={t(
                        'views.addons.addons.form.field.restrictedToDiets.tooltip'
                      )}
                      placement="right"
                    >
                      <Info fontSize="small" />
                    </Tooltip>
                  </FormLabel>
                  <SelectInput
                    noGrid={true}
                    multiple={true}
                    classes={classes}
                    name="restrictedToDiets"
                    mapBy="name"
                    trackBy="value"
                    options={diets.map(diet => ({
                      name: diet.name,
                      value: diet['@id'],
                    }))}
                    value={formData.restrictedToDiets}
                    handleChange={handleChange}
                    disabled={!canEditClientFields}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>
      <CardWrapper title={t('views.addons.addons.section.otherSettings.title')}>
        <CardBody>
          <GridContainer>
            <GridItem sm={8}>
              <GridContainer>
                <GridItem sm={4}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.cost.label')} *
                  </FormLabel>
                  <CustomInput
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      name: 'cost',
                      value: formData.cost,
                      onChange: handleChange,
                      type: 'number',
                      style: { marginTop: '9px' },
                    }}
                  />
                </GridItem>

                <GridItem sm={4}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.contractor.label')}
                  </FormLabel>
                  <SelectInput
                    noGrid={true}
                    classes={classes}
                    name="contractor"
                    mapBy="name"
                    options={contractors}
                    value={formData.contractor}
                    handleChange={handleChange}
                  />
                </GridItem>

                <GridItem sm={4}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.stock.label')}
                  </FormLabel>
                  <CustomInput
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      name: 'stock',
                      value: formData.stock,
                      onChange: handleChange,
                      type: 'number',
                      style: { marginTop: '9px' },
                    }}
                  />
                </GridItem>

                <GridItem sm={4}>
                  <FormLabel
                    className={classNames(
                      classes.labelHorizontal,
                      classes.codeLabel
                    )}
                  >
                    {t('form.field.code.label')} *
                  </FormLabel>
                  <CustomInput
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      name: 'code',
                      value: formData.code,
                      onChange: handleChange,
                    }}
                  />
                </GridItem>

                <GridItem sm={8}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.notes.label')}
                  </FormLabel>
                  <CustomInput
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      name: 'notes',
                      value: formData.notes,
                      onChange: handleChange,
                      style: { marginTop: '7px' },
                    }}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>

      <CardWrapper
        title={t('views.addons.addons.section.logisticSettings.title')}
      >
        <CardBody>
          <GridContainer>
            <GridItem sm={8}>
              <GridContainer>
                <GridItem sm={4}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.volume.label')}
                  </FormLabel>
                  <CustomInput
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      name: 'volume',
                      value: formData.volume,
                      onChange: handleChange,
                      type: 'number',
                      style: { marginTop: '9px' },
                    }}
                  />
                </GridItem>
                <GridItem sm={4}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('form.field.weight.label')}
                  </FormLabel>
                  <CustomInput
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      name: 'weight',
                      value: formData.weight,
                      onChange: handleChange,
                      type: 'number',
                      style: { marginTop: '9px' },
                    }}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>
    </>
  );
};

export default ClientPanel;
